import { useRouter } from 'next/router';
import * as React from 'react';
import { Fragment } from 'react';
import ContentSorterDropdown, { SortItem } from 'src/components/views/ContentSorterDropdown/ContentSorterDropdown';
import ScriptLdJson from 'src/components/views/ScriptLdJson/ScriptLdJson';
import { CategoryLoader, CategoryPageContentHeader, sendSubcategoryClick } from 'src/modules/Categories';
import { selectSelectedCategory } from 'src/redux/catalogue/selectors';
import { selectCategories } from 'src/redux/categories/categoriesSelectors';
import { useAppSelector } from 'src/redux/hooks';
import useChainService from 'src/services/chain/useChainService';
import useLocalization from 'src/services/localization/useLocalization';
import useStoreService from 'src/services/storeManagement/useStoreService';
import useBanners from 'src/swr/useBanners/useBanners';
import { useAppConfig } from 'src/utils/appConfig/useAppConfig';
import { getFirstLevelFilterTitle, getTitleWithFilter } from 'src/utils/categories/getCategorySeoMeta';
import { Filter, Product } from '../../../redux/apiTypes';
import getQueryWithMergedFilterParams from '../../../utils/filter/getQueryWithMergedFilterParams';
import { getDefaultSort, getSortList, navigateToSort } from '../../../utils/filter/sortContent';
import getCategoryLevel from '../../../utils/marketing/getCategoryLevel';
import Breadcrumbs from '../../views/Breadcrumbs/Breadcrumbs';
import Container from '../../views/Container/Container';
import Divider from '../../views/Divider/Divider';
import GoodsNumber from '../../views/GoodsNumber/GoodsNumber';
import MainSlider from '../../views/MainSlider/MainSlider';
import PageWrap from '../../views/PageWrap/PageWrap';
import PageWrapAside from '../../views/PageWrapAside/PageWrapAside';
import PageWrapBody from '../../views/PageWrapBody/PageWrapBody';
import Responsive from '../../views/Responsive/Responsive';
import NovaPoshtaSeoText from '../../views/SeoText/NovaPoshtaSeoText/NovaPoshtaSeoText';
import SeoText from '../../views/SeoText/SeoText';
import CatalogActiveFiltersList from '../CatalogActiveFiltersList/CatalogActiveFiltersList';
import getActiveFilters from '../CatalogActiveFiltersList/getActiveFilters';
import CatalogFilters from '../CatalogFilters/CatalogFilters';
import CategoryProductBox from '../CategoryProductBox/CategoryProductBox';
import EmptyCategory from '../EmptyCategory/EmptyCategory';
import LastViewProductsSlider from '../LastViewProductsSlider/LastViewProductsSlider';
import { styles } from './Category.styles';
import { getAggregateOffersMicroData, getBreadcrumbsItems } from './helpers';

const impressedListLevelMap = {
  1: 'First level category',
  2: 'Second level category',
  3: 'Third level category',
};

export interface CategoryProps {
  page: number
  excludeParams: string[]
  isFetchingProducts: boolean
  filters: Filter[],
  products: Product[],
  count: number,
  countAvailable: number,
  onLoadMore: () => void;
  onPageChange: (page: string) => void;
  minPrice?: number;
  maxPrice?: number;
}

interface QueryProps {
  'category-id': string;
  firstLevelFilter: string;
  lang: string;
  sort: string;
  page: string;
}

const Category = (props: CategoryProps) => {
  const localize = useLocalization();

  const {
    page,
    excludeParams,
    isFetchingProducts,
    filters,
    products,
    count,
    countAvailable,
    minPrice,
    maxPrice,
    onLoadMore,
    onPageChange,
  } = props;

  const isFetchingCategory = useAppSelector((s) => s.catalogue.isFetchingCategory);
  const language = useAppSelector((s) => s.app.language);
  const baseURL = useAppSelector((s) => s.app.baseURL);
  const selectedCategory = useAppSelector(selectSelectedCategory);
  const categories = useAppSelector(selectCategories);
  const store = useStoreService();
  const chain = useChainService();
  const { currency } = useAppConfig();

  const router = useRouter<QueryProps>();
  const { query, pathname } = router;

  const categoryLevel = getCategoryLevel(categories, selectedCategory.id);
  const categoryLevelString = impressedListLevelMap[categoryLevel];
  const queryWithMergedFilterParams = getQueryWithMergedFilterParams(query);
  const activeFilters = getActiveFilters(queryWithMergedFilterParams, filters);
  const firstLevelFilterTitle = getFirstLevelFilterTitle(query.firstLevelFilter, activeFilters);
  const breadcrumbsItems = React.useMemo(
    () => getBreadcrumbsItems({ categories, selectedCategory, firstLevelFilterTitle }),
    [categories, selectedCategory, firstLevelFilterTitle],
  );

  const loaderWrapperRef = React.useRef<HTMLDivElement>(null);

  const {
    data: banners,
    errors: bannersFetchingErrors,
    isFetching: isFetchingBanners,
  } = useBanners({
    place: 'category_page',
    storeId: store.id,
    categoryId: query['category-id'],
    canFetch: true,
  });

  const sortList = getSortList(pathname);
  const defaultSort = getDefaultSort(sortList);
  const activeSortKey = router.query.sort || defaultSort;
  const activeSort = sortList.find(s => s.value === activeSortKey);
  const sortFilters = sortList.map(s => ({
    label: localize(s.translation_key),
    value: s.value,
  }));

  const handleSort = (data: SortItem) => {
    navigateToSort(router, data.value, excludeParams);
  };

  const isEmptyCategory = !isFetchingProducts && Array.isArray(products) && products.length === 0;
  const iconSelectedCategory = selectedCategory.image_url?.svg || selectedCategory.image_url?.png;
  const h1Text = getTitleWithFilter(selectedCategory.title, firstLevelFilterTitle);

  const categoryMicroData = React.useMemo(() => getAggregateOffersMicroData({
    baseUrl: baseURL,
    currency,
    minPrice,
    maxPrice,
    count,
    products,
    categoryImage: iconSelectedCategory,
    categoryName: selectedCategory.title,
  }), [baseURL, count, currency, iconSelectedCategory, maxPrice, minPrice, products, selectedCategory.title]);

  const categoryPageIsLoading = isFetchingCategory || isFetchingProducts;
  const filtersAreEmpty = (!page || page === 1) && activeFilters.length === 0 ;

  return (
    <Container>
      <Breadcrumbs
        items={breadcrumbsItems}
        language={language}
        baseURL={baseURL}
        onBreadcrumbClick={(categoryId) => {
          sendSubcategoryClick({
            categoryId,
            page: `category_lvl${categoryLevel}`,
            place: 'category_bread_crumbs',
          });
        }}
      />
      <Divider />
      <CategoryPageContentHeader
        categoryLevel={categoryLevel}
        categories={categories}
        selectedCategory={selectedCategory}
        title={h1Text}
      />
      <PageWrap>
        <div ref={loaderWrapperRef} >
          {
            categoryPageIsLoading && <CategoryLoader topPositionInPx={loaderWrapperRef.current?.offsetTop} />
          }
        </div>

        <PageWrapAside>
          <CatalogFilters
            filters={filters}
            isFetchingProducts={isFetchingProducts}
            excludeParams={excludeParams}
          />
        </PageWrapAside>
        <PageWrapBody>

          <MainSlider
            place='category'
            language={language}
            banners={banners}
            isFetching={isFetchingBanners}
            hasError={!!bannersFetchingErrors?.length}
          />

          {!isEmptyCategory &&
            <div
              className="Category__underTitleContainer"
              data-marker='Under Title Block'
            >
              {countAvailable > 0 && (
                <div
                  className="Category__goodsNumber"
                  data-marker='Goods Number'
                >
                  <GoodsNumber number={countAvailable} />
                </div>
              )}

              <Responsive desktop>
                <ContentSorterDropdown
                  label={`${localize('filters.what-to-show')}:`}
                  filters={sortFilters}
                  onChange={handleSort}
                  active={{
                    label: localize(activeSort.translation_key),
                    value: activeSort.value,
                  }}
                />
              </Responsive>
            </div>
          }

          <CatalogActiveFiltersList
            filters={activeFilters}
            isFetchingProducts={isFetchingProducts}
            excludeParams={excludeParams}
          />

          {isEmptyCategory ? (
            <EmptyCategory
              excludeParams={excludeParams}
              activeFilters={activeFilters.length !== 0}
              iconSelectedCategory={iconSelectedCategory}
            />
          ) : (
            <CategoryProductBox
              products={products}
              totalCount={count}
              isFetchingProducts={isFetchingProducts}
              excludeParams={excludeParams}
              productPlace={categoryLevelString}
              onLoadMore={onLoadMore}
              onPageChange={onPageChange}
            />
          )}

          <div className='Category__lastVisitedSlider'>
            <LastViewProductsSlider />
          </div>

          {filtersAreEmpty &&
            <Fragment>
              {selectedCategory.description &&
                <SeoText content={selectedCategory.description} />
              }
              {(language !== 'en') && chain.novaPoshtaIsAvailable &&
                <NovaPoshtaSeoText
                  withTopMargin={!!selectedCategory.description}
                />
              }
            </Fragment>
          }
        </PageWrapBody>
        <ScriptLdJson body={categoryMicroData} />
      </PageWrap>

      <style jsx>{styles}</style>
    </Container>
  );
};

export default Category;
